<template>
  <div class="bg-white relative flex-1">
    <!-- top header start -->
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.cart-arrow-down-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Custom products') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app rounded-r-none" to="/admin/custom-products/new">
            {{ $t('New') }}
          </router-link>
          <div class="rounded-none shadow-sm">
            <button @click="openFiltersSidebar" class="inline-flex justify-center w-full rounded-none btn btn-app" id="filter-button" type="button">
              <svg-vue class="self-center mr-2 h-4 w-4" icon="font-awesome.filter-regular"></svg-vue>
              {{ $t('Filters') }}
              <svg-vue class="self-center ml-2.5 -mr-1.5 h-3 w-3" icon="font-awesome.chevron-right-solid"></svg-vue>
            </button>
            <!-- side filters -->
            <div class="fixed inset-0 overflow-hidden z-30" v-show="filtersSidebar">
              <div class="absolute inset-0 overflow-hidden">
                <section class="absolute inset-y-0 right-0 pl-10 max-w-full flex" v-on-clickaway="closeFiltersSidebar">
                  <transition
                    duration="500"
                    enter-active-class="transform transition ease-in-out duration-500"
                    enter-class="translate-x-full"
                    enter-to-class="translate-x-0"
                    leave-active-class="transform transition ease-in-out duration-500"
                    leave-class="translate-x-0"
                    leave-to-class="translate-x-full"
                  >
                    <div class="w-screen max-w-xs" v-show="filtersSidebar">
                      <div class="h-full flex flex-col space-y-4 bg-white shadow-xl">
                        <header class="px-4 pt-4 sm:px-6">
                          <div class="flex items-start justify-between space-x-3">
                            <h2 class="text-lg leading-7 font-medium text-gray-900">
                              {{ $t('Filters') }}
                            </h2>
                            <div class="h-7 flex items-center">
                              <button
                                @click="closeFiltersSidebar"
                                aria-label="Close panel"
                                class="text-gray-400 hover:text-gray-900 transition ease-in-out duration-150"
                                type="button"
                              >
                                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </header>
                        <div class="relative flex-1 px-4 sm:px-6 h-full border-t overflow-y-scroll">
                          <div class="absolute inset-0 px-4 sm:px-6">
                            <div class="grid grid-cols-3 py-3">
                              <div class="col-span-3 my-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="search"> {{ $t('Search') }}</label>
                                <div class="relative flex-grow focus-within:z-10">
                                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                  </div>
                                  <input
                                    :placeholder="$t('Search')"
                                    @change="getCustomProducts"
                                    aria-label="Search"
                                    class="form-input block w-full pl-10 text-sm transition ease-in-out duration-150"
                                    id="search"
                                    v-model.lazy="filters.search"
                                  />
                                </div>
                              </div>

                              <div class="col-span-3 my-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="label">{{ $t('Sort') }}</label>
                                <div class="relative inline-flex w-full">
                                  <button
                                    @click="changeSort"
                                    class="relative rounded-l-md inline-flex items-center px-4 py-2 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-900 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700"
                                    type="button"
                                  >
                                    <svg-vue
                                      class="h-5 w-5 text-gray-400"
                                      icon="font-awesome.sort-amount-down-alt-regular"
                                      v-show="sort.order === 'asc'"
                                    ></svg-vue>
                                    <svg-vue
                                      class="h-5 w-5 text-gray-400"
                                      icon="font-awesome.sort-amount-up-alt-regular"
                                      v-show="sort.order === 'desc'"
                                    ></svg-vue>
                                    <span class="ml-2">{{ $t('Sort') }}</span>
                                  </button>
                                  <select
                                    @change="changeSort"
                                    aria-label="Sort by"
                                    class="block form-select w-full pl-3 pr-9 py-2 rounded-none rounded-r-md border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-900 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                    id="sort.column"
                                    v-model="sort.column"
                                  >
                                    <option value="key">{{ $t('Tracking') }}</option>
                                    <option value="id">{{ $t('Order Number') }}</option>
                                    <option value="full_name">{{ $t('Full name') }}</option>
                                    <option value="phone">{{ $t('Phone') }}</option>
                                    <option value="email">{{ $t('Email') }}</option>
                                    <option value="imei">{{ $t('IMEI') }}</option>
                                    <option value="serial_number">{{ $t('Serial number') }}</option>
                                    <option value="created_at">{{ $t('Created at') }}</option>
                                    <option value="updated_at">{{ $t('Updated at') }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-span-3 my-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="label">{{ $t('Items per page') }}</label>
                                <input-select
                                  :options="[
                                    { id: 5, name: 5 },
                                    { id: 10, name: 10 },
                                    { id: 25, name: 25 },
                                    { id: 50, name: 50 },
                                  ]"
                                  @change="getCustomProducts"
                                  id="per_page"
                                  option-label="name"
                                  required
                                  v-model="perPage"
                                ></input-select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </section>
              </div>
            </div>
            <!-- end -->
          </div>
          <button @click="getCustomProducts" class="btn btn-app rounded-none" type="button">
            <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
          </button>
          <router-link to="/admin" class="btn btn-app rounded-none rounded-r-md">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <!-- top header ends -->

    <loading :status="loading"></loading>
    <!-- table start -->
    <div class="custom-products-list">
      <!-- table  has rows-->
      <template v-if="customProductList.length > 0">
        <div class="sm:hidden">
          <ul class="border-b border-green-200 divide-y divide-green-200">
            <template v-for="(product, index) in customProductList">
              <li :key="index">
                <router-link
                  :to="'/admin/custom-products/' + product.uuid + '/edit'"
                  class="flex items-center justify-between px-4 py-4 hover:bg-green-100 sm:px-6"
                >
                  <div class="flex items-center truncate space-x-3">
                    <div class="whitespace-no-wrap">
                      <div v-if="product.is_sold" class="text-sm leading-5 w-full truncate">
                        <span class="bg-green-300 px-1 mr-1">{{ $t('Set for Sale') }} </span>
                      </div>
                      <div class="text-sm leading-5 w-full truncate">
                        <span class="bg-gray-200 px-1 mr-1">ID # {{ product.id }} </span>
                      </div>
                      <div class="text-sm leading-5 w-full truncate">
                        <span class="bg-yellow-200 px-1"> {{ $t('SN #') }} : {{ product.serial_number }} </span><br />
                      </div>
                      <div class="text-sm leading-5 w-full truncate">
                        <span class="bg-yellow-200 px-1"> {{ $t('IMEI #') }} : {{ product.imei }} </span>
                      </div>
                      <div class="text-sm leading-5 w-full truncate">
                        <span class="bg-yellow-200 px-1">{{ $t('Device') }} : {{ product.model }}</span>
                      </div>

                      <div class="text-sm my-2 leading-5 text-gray-500 w-full truncate">
                        {{ $t('Cost') }} : {{ product.cost | currency }} <br />
                        {{ $t('Price') }} : {{ product.price | currency }} <br />
                      </div>
                      <div class="text-sm leading-5 text-gray-900 w-full truncate">
                        {{ $t('From') }} :
                        <strong>{{ product.full_name }}, {{ product.email }} </strong>
                      </div>
                      {{ product.updated_at  }}
                    </div>
                  </div>
                  <svg-vue
                    class="ml-4 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-600 transition ease-in-out duration-150"
                    icon="font-awesome.angle-right-regular"
                  ></svg-vue>
                </router-link>
              </li>
            </template>
          </ul>
        </div>
        <div class="hidden sm:block">
          <div class="align-middle inline-block min-w-full border-b border-green-200">
            <table class="min-w-full divide-y divide-green-200">
              <thead>
                <tr class="bg-green-500 text-white">
                  <th class="hidden lg:table-cell px-3 py-1 text-left text-xs leading-4 font-semibold uppercase tracking-wider">
                    {{ $t('Reference') }}
                  </th>
                  <th class="px-3 py-1 text-left text-xs leading-4 font-semibold uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                    {{ $t('Name') }}
                  </th>
                  <th class="px-3 py-1 text-left text-xs leading-4 font-semibold uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                    {{ $t('Cost') }}
                  </th>
                  <th class="px-3 py-1 text-left text-xs leading-4 font-semibold uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                    {{ $t('Price') }}
                  </th>
                  <th class="px-3 py-1 text-left text-xs leading-4 font-semibold uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                    {{ $t('Updated at') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-green-200">
                <template v-for="(product, index) in customProductList">
                  <router-link :to="'/admin/custom-products/' + product.uuid + '/edit'" class="cursor-pointer hover:bg-green-100" tag="tr">
                    <td class="hidden lg:table-cell px-3 py-1 whitespace-no-wrap leading-5">
                      <barcode :value="product.tracking" font-size="10" height="20"></barcode>
                    </td>
                    <td class="px-3 py-1 max-w-0 w-full whitespace-no-wrap">
                      <div class="flex text-sm leading-5 text-gray-900 w-full truncate">
                        <span v-if="product.is_sold" class="bg-green-200 px-1 mr-1"> {{ $t('Set for Sale') }} </span>
                        <span class="bg-gray-200 px-1 mr-1">ID # {{ product.id }} </span>
                        <span class="bg-yellow-200 px-1 mr-1">{{ $t('Device') }} : {{ product.model }}</span>
                        <span class="bg-yellow-200 px-1 mr-1"> {{ $t('SN #') }} : {{ product.serial_number }} </span>
                        <span class="bg-yellow-200 px-1 mr-1"> {{ $t('IMEI #') }} : {{ product.imei }} </span>
                      </div>
                      <div class="text-sm leading-5 text-gray-900 w-full truncate">
                        {{ $t('From') }} :
                        <strong>{{ product.full_name }}, {{ product.email }} </strong>
                      </div>
                    </td>
                    <td class="px-3 py-1 whitespace-no-wrap leading-5">
                      {{ product.cost | currency }}
                    </td>
                    <td class="px-3 py-1 whitespace-no-wrap leading-5">
                      {{ product.price | currency }}
                    </td>
                    <td class="px-3 py-1 whitespace-no-wrap leading-5">
                      <div class="text-sm text-gray-500">
                        {{ product.updated_at  }}
                      </div>
                    </td>
                  </router-link>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </template>
      <!-- end -->
      <!-- no rows -->
      <template v-else>
         <no-content :search="anyFilter"/>
      </template>
      <!-- end -->
    </div>
    <!-- table ends -->
    <!-- bottom nav start -->
    <nav class="bg-white absolute bottom-0 left-0 w-full px-4 py-2 flex items-center justify-between border-t border-gray-500 sm:px-6">
      <div class="hidden sm:block">
        <p class="text-sm leading-5 text-gray-700">
          {{ $t('Showing') }}
          <span class="font-medium">{{ pagination.perPage * pagination.currentPage - pagination.perPage + 1 }}</span>
          {{ $t('to') }}
          <span class="font-medium">{{
            pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total
          }}</span>
          {{ $t('of') }}
          <span class="font-medium">{{ pagination.total }}</span>
          {{ $t('results') }}
        </p>
      </div>
      <div class="flex-1 flex justify-between sm:justify-end">
        <button
          :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
          :disabled="pagination.currentPage <= 1"
          @click="changePage(pagination.currentPage - 1)"
          class="pagination-link"
          type="button"
        >
          {{ $t('Previous') }}
        </button>
        <button
          :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
          :disabled="pagination.currentPage >= pagination.totalPages"
          @click="changePage(pagination.currentPage + 1)"
          class="ml-3 pagination-link"
          type="button"
        >
          {{ $t('Next') }}
        </button>
      </div>
    </nav>
    <!-- bottom nav ends -->
  </div>
</template>
<script>
import { pickBy, identity } from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';
import VueBarcode from 'vue-barcode';

export default {
  name: 'list',
  components: {
    barcode: VueBarcode,
  },
  metaInfo() {
    return {
      title: this.$i18n.t('Custom products'),
    };
  },
  mixins: [clickaway],
  data() {
    return {
      loading: true,
      filtersSidebar: false,
      filters: {
        search: null,
        profile: null,
        customers: [],
      },

      sort: {
        order: 'desc',
        column: 'updated_at',
      },
      page: 1,
      perPage: 10,
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },

      customProductList: [],
    };
  },
  computed: {
    anyFilter() {
      return pickBy(this.filters, identity);
    },
  },
  mounted() {
    this.getCustomProducts();
  },
  methods: {
    openFiltersSidebar() {
      setTimeout(() => {
        this.filtersSidebar = true;
      }, 100);
    },
    closeFiltersSidebar() {
      this.filtersSidebar = false;
    },

    changePage(page) {
      if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
        this.page = page;
        this.getCustomProducts();
      }
    },
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getCustomProducts();
    },

    getCustomProducts() {
      this.loading = true;
      axios
        .get(route('custom-products.index'), {
          params: {
            page: this.page,
            sort: this.sort,
            perPage: this.perPage,
            ...this.filters,
          },
        })
        .then((response) => {
          this.customProductList = response.data.items;
          this.pagination = response.data.pagination;
          if (this.pagination.totalPages < this.pagination.currentPage) {
            this.page = this.pagination.totalPages;
            this.getCustomProducts();
          } else {
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style lang="scss">
.custom-products-list {
  @apply bg-white w-full z-0 overflow-y-auto;
  height: calc(100vh - 270px);
}

@media (min-width: theme('screens.md')) {
  .custom-products-list {
    height: calc(100vh - 170px);
  }
}

.filters-options {
  @apply rounded-md bg-white p-3 overflow-y-auto;
  height: calc(100vh - 270px);
  max-height: 360px;
}

@media (min-width: theme('screens.md')) {
  .filters-options {
    height: calc(100vh - 140px);
  }
}
</style>
